// @mui material components
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useParams } from "react-router-dom";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Soft UI Dashboard PRO React base styles
import typography from "assets/theme/base/typography";
import breakpoints from "assets/theme/base/breakpoints";
import ReportDetails from "./report/ReportDetails";
import ReportActions from "./report/ReportActions";
import ReportHistory from "./report/ReportHistory";
import ReportRedirection from "./report/ReportRedirection";

import useAPI from "utils/useApi";

function Default() {
  const { id } = useParams();
  const { values } = breakpoints;
  const { size } = typography;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.between("sm", "md"));
  const [activeTab, setActiveTab] = useState(0);

  const client = useAPI();
  
  
  // State for form fields
  const [status, setStatus] = useState();
  const [statusLabel, setStatusLabel] = useState("");
  const [redirectInstitution, setRedirectInstitution] = useState("");
  const [redirectReason, setRedirectReason] = useState("");
  const [message, setMessage] = useState("");
  const [sendNotification, setSendNotification] = useState(true);
  const [isStatusSolved, setIsStatusSolved] = useState("");
  const [complaintDetails, setComplaintDetails] = useState(null);
  const [formFetchData, setFormFetchData] = useState(null);
  
  const fetchFormData = async () => {
    try {
      const response = await client.getComplaintFormData();
      setFormFetchData(response);
    } catch (error) {
      console.error("Error fetching form data:", error);
    }
  };

  const fetchComplaintDetails = async () => {
    try {
        const response = await client.getComplaintFormDetails(id);
        // Ensure id is a string for prop validation
        setComplaintDetails({
          ...response,
          id: response.id ? String(response.id) : undefined
        });
        setStatus(response.status);
        setRedirectInstitution(response.institution_entity)
        // Set the label for the initial status
        if (response.formActionsData && response.formActionsData.status_choices) {
          const found = response.formActionsData.status_choices.find(opt => opt.value === response.status);
          setStatusLabel(found ? found.label : response.status);
        } else {
          setStatusLabel(response.status);
        }
    } catch (error) {
        console.error("Error fetching complaint details:", error);
    }
  };


  useEffect(() => {
    fetchComplaintDetails();
    fetchFormData();
  }, []);

  useEffect(() => {
    // Listen for status update events from child
    const handler = (e) => {
      if (e.detail && e.detail.status) {
        setStatus(e.detail.status);
        setComplaintDetails((prev) => {
          if (!prev) return prev;
          // Try to get the label for the new status from the last known status_choices
          let label = e.detail.status;
          // Use formActionsData from ReportActions if available, otherwise fallback to prev.formActionsData
          let statusChoices = (prev.formActionsData && prev.formActionsData.status_choices) || [];
          if (statusChoices.length === 0 && window._lastStatusChoices) {
            statusChoices = window._lastStatusChoices;
          }
          const found = statusChoices.find(opt => opt.value === e.detail.status);
          setStatusLabel(found ? found.label : e.detail.status);
          return { ...prev, status: e.detail.status };
        });
        setIsStatusSolved(e.detail.status === "Soluționată");
      }
    };
    window.addEventListener('complaint-status-updated', handler);
    return () => window.removeEventListener('complaint-status-updated', handler);
  }, []);

  useEffect(() => {
    // Listen for status_choices from ReportActions and cache globally for label lookup
    const handler = (e) => {
      if (e.detail && e.detail.status_choices) {
        window._lastStatusChoices = e.detail.status_choices;
      }
    };
    window.addEventListener('complaint-status-choices', handler);
    return () => window.removeEventListener('complaint-status-choices', handler);
  }, []);

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    setStatus(newStatus);
    setIsStatusSolved(newStatus === "Soluționată");
  };
  
  const getStatusColor = (status) => {
    // Only allow valid MUI Chip color values
    switch (statusLabel || status) {
      case "În așteptare":
        return "warning";
      case "Cerere înregistrată":
        return "info";
      case "Necesită informații suplimentare":
        return "warning";
      case "Redirecționat":
        return "primary";
      case "Cerere soluționată":
        return "success";
      case "Nevalidat":
        return "error";
      default:
        return "default";
    }
  };

  // Helper functions to get human-readable labels from values (using Romanian labels from backend)
  const getIncidentTypeLabel = (value) => {
    if (!value) return value;
    if (!formFetchData?.incident_types) return value; // Return original value if data not loaded yet
    const type = formFetchData.incident_types.find(t => t.value === value);
    return type ? type.label : value; // Return Romanian label from backend, or original value if not found
  };

  const getInjuryTypeLabel = (value) => {
    if (!value) return value;
    if (!formFetchData?.injury_types) return value;
    const type = formFetchData.injury_types.find(t => t.value === value);
    return type ? type.label : value;
  };

  const getResponsibleEntityLabel = (value) => {
    if (!value) return value;
    if (!formFetchData?.responsible_entity_types) return value;
    const type = formFetchData.responsible_entity_types.find(t => t.value === value);
    return type ? type.label : value;
  };

  const getIncidentPlaceTypeLabel = (value) => {
    if (!value) return value;
    if (!formFetchData?.incident_place_types) return value;
    const type = formFetchData.incident_place_types.find(t => t.value === value);
    return type ? type.label : value;
  };

  const getForeignSubmissionResponsibleLabel = (value) => {
    if (!value) return value;
    if (!formFetchData?.foreign_submission_responsible_types) return value;
    const type = formFetchData.foreign_submission_responsible_types.find(t => t.value === value);
    return type ? type.label : value;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePrint = () => {
    if (!complaintDetails) return;

    const formatD = (raw) => {
      if (!raw) return "—";
      try {
        return new Intl.DateTimeFormat("ro-RO", {
          day: "2-digit", month: "long", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        }).format(new Date(raw));
      } catch { return raw; }
    };

    const row = (label, value) => `
      <tr>
        <td style="padding:7px 12px;font-weight:600;color:#555;width:220px;border-bottom:1px solid #f0f0f0;font-size:12px">${label}</td>
        <td style="padding:7px 12px;color:#0f2c4a;border-bottom:1px solid #f0f0f0;font-size:12px">${value || "—"}</td>
      </tr>`;

    const html = `<!DOCTYPE html>
<html lang="ro">
<head>
  <meta charset="UTF-8"/>
  <title>Sesizare ${complaintDetails.uuid || ""}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; color: #1a1a1a; background: white; padding: 32px; }
    h1 { font-size: 20px; color: #0f2c4a; margin-bottom: 4px; }
    .subtitle { font-size: 12px; color: #666; margin-bottom: 24px; }
    .section { margin-bottom: 24px; }
    .section-title { background: linear-gradient(135deg,#0f2c4a,#1565C0); color: white; padding: 8px 14px; font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; border-radius: 6px 6px 0 0; }
    table { width: 100%; border-collapse: collapse; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 6px 6px; overflow: hidden; }
    .desc-box { border: 1px solid #e0e0e0; border-top: none; padding: 12px 14px; font-size: 12px; color: #344767; line-height: 1.7; border-radius: 0 0 6px 6px; white-space: pre-wrap; }
    .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 10px; color: #999; text-align: center; }
    @media print { body { padding: 16px; } }
  </style>
</head>
<body>
  <h1>Sesizare #${complaintDetails.uuid || "N/A"}</h1>
  <p class="subtitle">Document generat la ${formatD(new Date().toISOString())} &nbsp;|&nbsp; Status: ${statusLabel || complaintDetails.status || "—"}</p>

  <div class="section">
    <div class="section-title">Informații generale</div>
    <table>
      ${row("Cod sesizare", complaintDetails.uuid)}
      ${row("Data depunerii", formatD(complaintDetails.created_at))}
      ${row("Instituție curentă", complaintDetails.institution_entity)}
      ${row("Tip incident", getIncidentTypeLabel(complaintDetails.incident_type))}
      ${row("Vătămare", getInjuryTypeLabel(complaintDetails.injury_type))}
      ${row("Persoană reclamată", getResponsibleEntityLabel(complaintDetails.responsible))}
      ${row("Locul faptei", complaintDetails.deed_location)}
      ${row("Loc incident", getIncidentPlaceTypeLabel(complaintDetails.incident_place_type))}
      ${row("Sesizare depusă anterior", complaintDetails.foreign_submission === "yes" ? "Da" : complaintDetails.foreign_submission === "no" ? "Nu" : "—")}
      ${complaintDetails.foreign_submission === "yes" ? row("Sesizată anterior la", getForeignSubmissionResponsibleLabel(complaintDetails.foreign_submission_responsible)) : ""}
      ${row("Confidențialitate", complaintDetails.confidential_data === true ? "Da" : complaintDetails.confidential_data === false ? "Nu" : "—")}
    </table>
  </div>

  <div class="section">
    <div class="section-title">Date petent</div>
    <table>
      ${row("Nume și prenume", complaintDetails.name)}
      ${row("Adresă e-mail", complaintDetails.email)}
      ${row("Telefon", complaintDetails.phone)}
      ${row("Data nașterii", complaintDetails.date_of_birth)}
      ${row("Adresă fizică", complaintDetails.address)}
    </table>
  </div>

  <div class="section">
    <div class="section-title">Descrierea detaliată a sesizării</div>
    <div class="desc-box">${complaintDetails.description || "—"}</div>
  </div>

  <div class="footer">Platformă Națională de Sesizări &nbsp;|&nbsp; Document oficial &nbsp;|&nbsp; Stop Rasism</div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=900,height=700");
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={2}>
        <Card>
          {complaintDetails ? (
            <>
              <SoftBox p={2} display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"}>
                <SoftBox mb={isMobile ? 1 : 0}>
                  <SoftTypography variant="h5" fontWeight="medium">
                    Sesizare {complaintDetails.uuid}
                  </SoftTypography>
                  <SoftBox display="flex" alignItems="center" mt={0.5}>
                    {complaintDetails.status && (
                      <Chip
                        size="small"
                        label={statusLabel || complaintDetails.status}
                        color={getStatusColor(complaintDetails.status)}
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    )}
                    <SoftTypography variant="caption" fontWeight="medium" ml={1}>
                      {complaintDetails.created_at ? new Intl.DateTimeFormat("ro-RO", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(complaintDetails.created_at)) : "—"} • {getIncidentTypeLabel(complaintDetails.incident_type)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <SoftBox display="flex" gap={1}>
                  <SoftButton variant="outlined" color="secondary" size="small" onClick={handlePrint}>
                    <Icon sx={{ mr: 0.5 }}>print</Icon>
                    {!isMobile && "Printează / PDF"}
                  </SoftButton>
                </SoftBox>
              </SoftBox>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons={isMobile ? "auto" : false}
                sx={{ 
                  px: 2, 
                  borderBottom: "1px solid #eee",
                  '& .MuiTab-root': { minHeight: '48px', fontSize: isMobile ? '0.75rem' : '0.875rem' }
                }}
              >
                <Tab label="Detalii sesizare" />
                <Tab label="Acțiuni instituție" />
                <Tab label="Istoric" />
                <Tab label="Redirectionari" />
              </Tabs>
              {activeTab === 0 && (
                <ReportDetails 
                  reportData={complaintDetails} 
                  isMobile={isMobile} 
                  apiClient={client}
                  getIncidentTypeLabel={getIncidentTypeLabel}
                  getInjuryTypeLabel={getInjuryTypeLabel}
                  getResponsibleEntityLabel={getResponsibleEntityLabel}
                  getIncidentPlaceTypeLabel={getIncidentPlaceTypeLabel}
                  getForeignSubmissionResponsibleLabel={getForeignSubmissionResponsibleLabel}
                />
              )}
              {activeTab === 1 && (
                <ReportActions
                  reportData={complaintDetails}
                  status={status}
                  setStatus={setStatus}
                  redirectInstitution={redirectInstitution}
                  setRedirectInstitution={setRedirectInstitution}
                  redirectReason={redirectReason}
                  setRedirectReason={setRedirectReason}
                  message={message}
                  setMessage={setMessage}
                  isStatusSolved={!!isStatusSolved}
                  sendNotification={sendNotification}
                  setSendNotification={setSendNotification}
                  isMobile={isMobile}
                  refreshDetails={fetchComplaintDetails}
                />
              )}
              {activeTab === 2 && (
                <ReportHistory timeline={complaintDetails.timeline || []} isMobile={isMobile} />
              )}
              {activeTab === 3 && (
                <ReportRedirection redirections={complaintDetails.redirections || []} isMobile={isMobile} />
              )}
            </>
          ) : (
            <SoftBox p={4} textAlign="center">
              <SoftTypography variant="h6" color="text">Se încarcă detaliile sesizării...</SoftTypography>
            </SoftBox>
          )}
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Default;
